import { useState } from 'react'
import { toast } from 'react-toastify'
import { Container, ContainerContent } from 'components/Containers/Containers'
import { HonestTable, IHonestTableColumn } from 'components/HonestTable/HonestTable'
import { Badge } from 'components/Badge/Badge'
import { Button, Flex } from 'styling/GlobalStyles'
import { BodyText } from 'styling/Typography'
import { Cell } from 'components/HonestTable/HonestTable.styled'
import { IKitchenDishesTabProps } from 'types/kitchen/kitchenTabProps.interface'
import { IKitchenDish } from 'types/kitchen/kitchenDish.interface'
import { KitchenTag } from '../Kitchen.styled'
import { NewDishModal } from '../modals/NewDishModal'
import { RecipeModal } from '../modals/RecipeModal'

export const DishesTab = ({
  dishes,
  setDishes,
  ingredients,
  costPerPortion,
  createOpen,
  setCreateOpen,
}: IKitchenDishesTabProps) => {
  const [editDish, setEditDish] = useState<IKitchenDish | null>(null)

  const columns: IHonestTableColumn<IKitchenDish>[] = [
    {
      id: 'name',
      header: 'Dish',
      sticky: true,
      minWidth: '14rem',
      cell: (d) => (
        <Cell>
          <BodyText weight='600'>{d.name}</BodyText>
          {d.supplier && <BodyText grey>Supplier: {d.supplier}</BodyText>}
        </Cell>
      ),
    },
    {
      id: 'type',
      header: 'Type',
      cell: (d) => (
        <Badge text={d.type} blue={d.type === 'Prepared'} green={d.type === 'Purchased'} />
      ),
    },
    {
      id: 'tags',
      header: 'Dietary tags',
      cell: (d) => (
        <Flex wrap gap='0.25rem'>
          {d.tags.map((t) => (
            <KitchenTag key={t}>{t}</KitchenTag>
          ))}
        </Flex>
      ),
    },
    {
      id: 'cost',
      header: 'Cost / portion',
      cell: (d) => <BodyText>{d.norm.length ? `$${costPerPortion(d).toFixed(2)}` : '—'}</BodyText>,
    },
    {
      id: 'status',
      header: 'Status',
      cell: (d) => (
        <Badge text={d.active ? 'Active' : 'Inactive'} green={d.active} orange={!d.active} />
      ),
    },
    {
      id: 'actions',
      header: '',
      align: 'right',
      cell: (d) =>
        d.type === 'Prepared' ? (
          <Flex justend width='100%'>
            <Button orangeBorder onClick={() => setEditDish(JSON.parse(JSON.stringify(d)))}>
              Recipe
            </Button>
          </Flex>
        ) : null,
    },
  ]

  const handleNewDish = (dish: IKitchenDish, active: boolean) => {
    setDishes((ds) => [...ds, dish])
    toast.success(
      active
        ? `${dish.name} created and ACTIVE — ${dish.type === 'Prepared' ? 'normative v1 saved; available to Menu Planner' : `purchased dish; supplier ${dish.supplier}`}`
        : `${dish.name} saved as DRAFT (inactive)`,
    )
    setCreateOpen(false)
  }

  const handleRecipeSave = (dish: IKitchenDish) => {
    setDishes((ds) => ds.map((d) => (d.id === dish.id ? dish : d)))
    toast.success(`Normative v2 saved for ${dish.name} — effective from next production day`)
    setEditDish(null)
  }

  return (
    <Flex column gap='1rem' width='100%'>
      <Container>
        <ContainerContent>
          <HonestTable columns={columns} data={dishes} keyField='id' />
        </ContainerContent>
      </Container>
      {createOpen && (
        <NewDishModal
          ingredients={ingredients}
          dishes={dishes}
          closeModal={() => setCreateOpen(false)}
          onSave={handleNewDish}
        />
      )}
      {editDish && (
        <RecipeModal
          dish={editDish}
          ingredients={ingredients}
          closeModal={() => setEditDish(null)}
          onSave={handleRecipeSave}
        />
      )}
    </Flex>
  )
}
